import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import {
	listIncidents,
	IncidentListItem,
	IncidentListWithConnection,
	IncidentsLoadStatus
} from "client/Api/Workspace/LiveInformation/TopDesk/topDeskLiveInformation";
import {
	LiveInformationProvider,
	InterestedParty,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { TopDeskConnectionIdentifierViewModel } from "client/Api/Apps/EditApp/saveApp";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

interface TopDeskLiveInformationProviderOptions {
	intervalInSeconds: number;
}

type TopDeskConnectionInterests = {
	items?: true;
};

interface IntrestedTopDeskParty extends InterestedParty<number, TopDeskConnectionIdentifierViewModel> {
	updateItems(items: LazyLoadPageResult<IncidentListItem>): void;
	informAboutTopDeskNotBeingConfigured(): void;
	informAboutGlobalRetrievalFailure(): void;
}

class TopDeskLiveInformationProvider extends LiveInformationProvider<
	TopDeskLiveInformationProviderOptions,
	number,
	TopDeskConnectionIdentifierViewModel,
	TopDeskConnectionInterests,
	IntrestedTopDeskParty,
	IncidentListWithConnection
> {

	constructor(options: TopDeskLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[TopDeskConnectionIdentifierViewModel, TopDeskConnectionInterests]>
	): Promise<ReadonlyArray<[TopDeskConnectionIdentifierViewModel, IncidentListWithConnection]>> {
		const connections: TopDeskConnectionIdentifierViewModel[] = entries.map(x => x[0]);
		const incidentListsByConnection: IncidentListWithConnection[] = await listIncidents(connections);
		return incidentListsByConnection.map(x => [x.connection, x]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<TopDeskConnectionInterests, IncidentListWithConnection>,
		response: [TopDeskConnectionInterests, IncidentListWithConnection]
	) {
		const data: IncidentListWithConnection = response[1];

		cacheItem.retrievedInterests.items = true;
		cacheItem.data = data;
	}

	protected informParty(party: IntrestedTopDeskParty, cacheItem: [TopDeskConnectionInterests, IncidentListWithConnection]) {
		const data: IncidentListWithConnection = cacheItem[1];
		switch(data.status) {
			case IncidentsLoadStatus.NoError:
				party.updateItems(data.items);
				break;
			case IncidentsLoadStatus.NotConfigured:
				party.informAboutTopDeskNotBeingConfigured();
				break;
			case IncidentsLoadStatus.AccessDenied:
			case IncidentsLoadStatus.UnknownError:
				party.informAboutGlobalRetrievalFailure();
				break;
			default:
				throw new UnreachableCaseError(data.status);
		}
	}

	protected override createResourceMap<TValue>(): Map<TopDeskConnectionIdentifierViewModel, TValue> {
		return new NormalizedMap<TopDeskConnectionIdentifierViewModel, TValue>(
			normalizeKey
		);
	}

	public reload(party: IntrestedTopDeskParty) {
		this.clearCacheForResource(party.resourceId);
		const interests: TopDeskConnectionInterests = this.getInterestsForEnsuredParty(party);
		this.queuePartyForDataRetrieval(party, interests);
	}
}

function normalizeKey(key: TopDeskConnectionIdentifierViewModel): string {
	const DEFAULT_CONNECTION_KEY = "default";

	if (key.isDefault === true) {
		return DEFAULT_CONNECTION_KEY;
	}

	return `connection_${key.id}`;
}

export {
	TopDeskLiveInformationProvider,
	type IntrestedTopDeskParty
};
